/**
 * quoteInternalNotes — IDI-305 (BUILD CONTRACT 2026-10-20).
 *
 * A quote's internal notes are an append-only, attributed LIST of
 * { id, content, author, author_id, created_at }, written only by the
 * server's ?action=add_note (which stamps author and time from the login).
 * Internal only: never on the customer document, PDF, signing page or email.
 *
 * The server already normalizes on read (cmd/quotes quote_notes.go). This
 * mirror is defensive: whatever shape arrives (legacy string, list, junk),
 * the Notes tab never renders blank-for-a-real-note or "[object Object]"
 * (the IDI-123 / IDI-278 class).
 */

export const LEGACY_NOTE_AUTHOR = 'Earlier note (author not recorded)';

function toText(v) {
  if (typeof v === 'string') return v;
  if (v == null) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/**
 * @param {*} raw - the quote's internal_notes as received
 * @param {object} [quote] - the quote (for id + createdAt on a legacy string)
 * @returns {Array<{id, content, author, author_id, created_at}>}
 */
export function normalizeInternalNotes(raw, quote = {}) {
  const createdAt = quote.createdAt || quote.created_at || null;
  if (typeof raw === 'string') {
    if (!raw.trim()) return [];
    return [{
      id: `legacy-${quote.id || 'quote'}`,
      content: raw,
      author: LEGACY_NOTE_AUTHOR,
      author_id: null,
      created_at: createdAt,
    }];
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((n, i) => {
      if (typeof n === 'string') {
        if (!n.trim()) return null;
        return { id: `legacy-${quote.id || 'quote'}-${i}`, content: n, author: LEGACY_NOTE_AUTHOR, author_id: null, created_at: createdAt };
      }
      if (!n || typeof n !== 'object') return null;
      const content = toText(n.content ?? n.text ?? n.body);
      if (!content.trim()) return null;
      return {
        id: n.id || `note-${i}`,
        content,
        author: toText(n.author) || LEGACY_NOTE_AUTHOR,
        author_id: n.author_id ?? null,
        created_at: n.created_at || n.createdAt || null,
        // IDI-552: an edited note carries its edit time; shown + sorted by it.
        ...((n.updated_at || n.updatedAt) ? { updated_at: n.updated_at || n.updatedAt } : {}),
      };
    })
    .filter(Boolean);
}

/**
 * IDI-552: the time a note is shown and sorted by — its edit time when it
 * was edited, else its creation time. No "edited" label (Adrian 10-21).
 */
export function noteShownAt(n) {
  return n?.updated_at || n?.updatedAt || n?.created_at || n?.createdAt || '';
}

/** Newest first by updated_at || created_at. Undated notes sink to the bottom. Stable for ties. */
export function sortNotesNewestFirst(notes) {
  const ts = (n) => {
    const t = Date.parse(noteShownAt(n));
    return Number.isNaN(t) ? -Infinity : t;
  };
  return (Array.isArray(notes) ? notes : [])
    .map((n, i) => ({ n, i }))
    .sort((a, b) => (ts(b.n) - ts(a.n)) || (b.i - a.i))
    .map(({ n }) => n);
}

export default normalizeInternalNotes;

/**
 * IDI-552: the toast for a failed edit/delete. 403 = not the author; 404 =
 * the note is gone; 409 = it changed in another tab. The last two ask for a
 * reload.
 */
export function noteActionErrorMessage(err, verb = 'update') {
  const status = err?.response?.status ?? err?.status;
  if (status === 403) return "Only the note's author can edit or delete it.";
  if (status === 404) return 'This note was already deleted. Reload to see the latest notes.';
  if (status === 409) return 'This note changed in another tab. Reload to see the latest notes.';
  return `Failed to ${verb} note`;
}
